import { useState, useEffect, useRef } from 'react';
import { superadminApi } from '../lib/api';

const SANS = "'Roboto', system-ui, sans-serif";

export type VendorUploadResponse = {
  rowsImported: number;
  rowsSkipped: number;
  skippedReasons?: Array<{ row: number; reason: string }>;
  currenciesFound?: string[];
};

interface Props {
  tenantId: string;
  tenantName?: string;
  /** Optional: show a tenant picker (legacy behaviour on SavingsDashboardPage). */
  tenantPicker?: {
    tenants: Array<{ id: string; name: string }>;
    selectedId: string;
    onChange: (id: string) => void;
  };
  onClose: () => void;
  /** Fired after a successful import with rowsImported > 0. */
  onSuccess?: (result: VendorUploadResponse) => void;
}

export default function UploadVendorDataModal({
  tenantId, tenantName, tenantPicker, onClose, onSuccess,
}: Props) {
  const [file, setFile]                 = useState<File | null>(null);
  const [fileError, setFileError]       = useState<string | null>(null);
  const [uploading, setUploading]       = useState(false);
  const [result, setResult]             = useState<VendorUploadResponse | null>(null);
  const [uploadError, setUploadError]   = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!result || (result.rowsImported ?? 0) === 0) return;
    onSuccess?.(result);
    const timer = setTimeout(() => onClose(), 2000);
    return () => clearTimeout(timer);
  }, [result, onClose, onSuccess]);

  // Body-scroll lock: prevents double-scroll behaviour while this modal is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const handleUpload = async () => {
    if (!file || !tenantId) return;
    setUploading(true); setUploadError(null); setResult(null);
    try { setResult(await superadminApi.uploadVendors(tenantId, file)); }
    catch (err) { setUploadError(err instanceof Error ? err.message : 'Upload failed'); }
    finally { setUploading(false); }
  };

  return (
    <div
      style={overlayStyle}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={modalStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <p style={{ margin: 0, fontSize: '1rem', fontWeight: 400, color: '#0f172a' }}>Upload Vendor Data</p>
            <p style={{ margin: '0.2rem 0 0', fontSize: '0.75rem', color: '#64748b' }}>
              Import vendors from an Excel spreadsheet (.xlsx)
              {tenantName ? ` — ${tenantName}` : ''}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1.2rem', lineHeight: 1, padding: '0.2rem' }}>×</button>
        </div>

        {tenantPicker && (
          <div style={{ marginBottom: '1rem' }}>
            <p style={labelStyle}>Client Tenant</p>
            <select value={tenantPicker.selectedId} onChange={(e) => tenantPicker.onChange(e.target.value)} style={selectStyle}>
              {tenantPicker.tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}

        <div style={{ marginBottom: '1.25rem' }}>
          <p style={labelStyle}>Excel File (.xlsx)</p>
          <div
            onClick={() => fileRef.current?.click()}
            style={{
              border: `1px dashed ${fileError ? '#ef4444' : file ? '#0ea5e9' : '#cbd5e1'}`,
              borderRadius: 8, padding: '0.85rem 1rem', cursor: 'pointer',
              backgroundColor: fileError ? '#fee2e2' : file ? '#e0f2fe' : '#f8fafc',
              transition: 'background 0.15s, border-color 0.15s',
            }}
          >
            <p style={{ margin: 0, fontSize: '0.82rem', color: fileError ? '#991b1b' : file ? '#0369a1' : '#94a3b8' }}>
              {file ? file.name : 'Click to select .xlsx file'}
            </p>
          </div>
          {fileError && <p style={{ margin: '0.35rem 0 0', fontSize: '0.75rem', color: '#ef4444' }}>{fileError}</p>}
          <input
            ref={fileRef} type="file" accept=".xlsx" style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setFileError(null); setResult(null); setUploadError(null);
              if (!f) { setFile(null); return; }
              if (!f.name.toLowerCase().endsWith('.xlsx')) {
                setFileError('Only .xlsx files are accepted. Please select a valid Excel file.');
                setFile(null); e.target.value = ''; return;
              }
              if (f.size > 10 * 1024 * 1024) {
                setFileError('File size exceeds 10MB limit. Please use a smaller file.');
                setFile(null); e.target.value = ''; return;
              }
              setFile(f);
            }}
          />
        </div>

        {!result && (
          <button
            onClick={handleUpload} disabled={!file || !tenantId || uploading}
            style={{
              width: '100%', padding: '0.7rem', borderRadius: 8, border: 'none',
              backgroundColor: !file || !tenantId || uploading ? '#cbd5e1' : '#0ea5e9',
              color: !file || !tenantId || uploading ? '#94a3b8' : '#ffffff',
              fontSize: '0.875rem', fontWeight: 400,
              cursor: !file || !tenantId || uploading ? 'not-allowed' : 'pointer',
              fontFamily: SANS,
            }}
          >
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        )}

        {uploadError && (
          <div style={{ marginTop: '1rem', backgroundColor: '#fee2e2', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '0.75rem', color: '#991b1b', fontSize: '0.82rem' }}>
            {uploadError}
          </div>
        )}

        {result && (
          <div style={{ marginTop: '0.5rem' }}>
            {(result.rowsImported ?? 0) === 0 ? (
              <div style={{ backgroundColor: '#fef3c7', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, padding: '0.85rem 1rem', marginBottom: '0.75rem' }}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: '#92400e', fontWeight: 400 }}>
                  ⚠️ No vendors imported. Check the Excel file has data rows below the separator.
                </p>
              </div>
            ) : (
              <div style={{ backgroundColor: '#d1fae5', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 8, padding: '0.85rem 1rem', marginBottom: '0.75rem' }}>
                <p style={{ margin: '0 0 0.25rem', fontSize: '0.875rem', color: '#065f46', fontWeight: 400 }}>
                  ✅ Imported {result.rowsImported ?? 0} vendor{(result.rowsImported ?? 0) !== 1 ? 's' : ''}
                  {(result.currenciesFound?.length ?? 0) > 0 && ` (${result.currenciesFound!.join(', ')} detected)`}
                  {(result.rowsSkipped ?? 0) > 0 && ` · ${result.rowsSkipped} rows skipped`}
                  {tenantName ? ` for ${tenantName}` : ''}.
                </p>
                <p style={{ margin: 0, fontSize: '0.78rem', color: '#64748b' }}>Closing in a moment…</p>
                {(result.skippedReasons?.length ?? 0) > 0 && (
                  <div style={{ marginTop: '0.5rem' }}>
                    {result.skippedReasons!.slice(0, 3).map((e, i) => (
                      <p key={i} style={{ margin: '0.15rem 0', fontSize: '0.72rem', color: '#ef4444' }}>{e.reason}</p>
                    ))}
                    {result.skippedReasons!.length > 3 && (
                      <p style={{ margin: '0.15rem 0', fontSize: '0.72rem', color: '#94a3b8' }}>…and {result.skippedReasons!.length - 3} more</p>
                    )}
                  </div>
                )}
              </div>
            )}
            <button onClick={onClose} style={{ width: '100%', padding: '0.55rem', borderRadius: 8, border: '1px solid #e2e8f0', background: 'none', color: '#64748b', fontSize: '0.82rem', cursor: 'pointer', fontFamily: SANS }}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
  backgroundColor: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  padding: '60px 20px 60px 20px',
  overflowY: 'auto',
};

const modalStyle: React.CSSProperties = {
  backgroundColor: '#ffffff', border: '1px solid #e2e8f0',
  borderRadius: 14, padding: '2rem', width: '100%', maxWidth: '480px',
  maxHeight: 'calc(100vh - 120px)',
  overflowY: 'auto',
  display: 'flex', flexDirection: 'column',
  fontFamily: SANS, boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
};

const labelStyle: React.CSSProperties = {
  margin: '0 0 0.4rem', fontSize: '0.72rem', fontWeight: 500, color: '#64748b',
  textTransform: 'uppercase', letterSpacing: '0.06em',
};

const selectStyle: React.CSSProperties = {
  width: '100%', backgroundColor: '#ffffff', border: '1px solid #e2e8f0',
  borderRadius: 8, padding: '0.45rem 0.75rem',
  color: '#0f172a', fontSize: '0.85rem', cursor: 'pointer', outline: 'none', fontFamily: SANS,
};
